import { ClientPortfolioPaymentFrequencyEnum } from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { getCoDebtorsShape } from "web_myplann/Portfolio/Forms/CoDebtors/coDebtorsSchema";
import { refineContractRequiredMinimum } from "web_myplann/Portfolio/Forms/ContractShared/contractRequiredMinimum";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { optionalDateAsString } from "web_myplann/Shared/Validation/optionalDateAsString";
import { optionalPositiveFloat } from "web_myplann/Shared/Validation/optionalPositiveFloat";
import { refineDateOrder } from "web_myplann/Shared/Validation/refineDateOrder";
import { trimmedNullable } from "web_myplann/Shared/Validation/trimmedNullable";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

const getBusinessLoansBaseShape = (t: TranslateFunction) => ({
	name: trimmedNullable(
		z.string().max(
			ValidationLimits.TextLength.LongName,
			t(Resources.Portfolio.AddShared.Validations.maxLength, {
				max: ValidationLimits.TextLength.LongName,
			}),
		),
	),
	contractNumber: trimmedNullable(
		z.string().max(
			ValidationLimits.TextLength.Name,
			t(Resources.Portfolio.AddShared.Validations.maxLength, {
				max: ValidationLimits.TextLength.Name,
			}),
		),
	),
	producer: trimmedNullable(
		z.string().max(
			ValidationLimits.TextLength.LongName,
			t(Resources.Portfolio.AddShared.Validations.maxLength, {
				max: ValidationLimits.TextLength.LongName,
			}),
		),
	),
	product: trimmedNullable(
		z.string().max(
			ValidationLimits.TextLength.LongName,
			t(Resources.Portfolio.AddShared.Validations.maxLength, {
				max: ValidationLimits.TextLength.LongName,
			}),
		),
	),
	signedAt: optionalDateAsString(DateFormat.Iso),
	startAt: optionalDateAsString(DateFormat.Iso),
	endAt: optionalDateAsString(DateFormat.Iso),
	value: optionalPositiveFloat(ValidationLimits.Amount.Max, {
		positiveMessage: t(
			Resources.Portfolio.AddShared.Validations.positiveNumber,
		),
		maxMessage: t(Resources.Portfolio.AddShared.Validations.maxValue),
	}),
	coDebtors: getCoDebtorsShape(t),
});

const getBusinessLoansChangeShape = (t: TranslateFunction) => {
	const maxMessage = t(Resources.Portfolio.AddShared.Validations.maxValue);
	const positiveNumberMessages = {
		positiveMessage: t(
			Resources.Portfolio.AddShared.Validations.positiveNumber,
		),
		maxMessage,
	};

	return z.object({
		originalObligationAmount: optionalPositiveFloat(
			ValidationLimits.Amount.Max,
			positiveNumberMessages,
		),
		installment: optionalPositiveFloat(
			ValidationLimits.Amount.Max,
			positiveNumberMessages,
		),
		interestRate: z
			.number()
			.gt(0, t(Resources.Portfolio.AddShared.Validations.positiveNumber))
			.max(
				ValidationLimits.Amount.MaxInterestRate,
				t(Resources.Portfolio.AddShared.Validations.maxValue),
			)
			.nullish()
			.transform((val) => val ?? null),
		fixationEndDate: optionalDateAsString(DateFormat.Iso),
		paymentFrequency: z
			.nativeEnum(ClientPortfolioPaymentFrequencyEnum)
			.nullable(),
	});
};

const getDateOrderMessages = (t: TranslateFunction) => ({
	startBeforeSigned: t(
		Resources.Portfolio.AddShared.Validations.startBeforeSigned,
	),
	endBeforeSigned: t(Resources.Portfolio.AddShared.Validations.endBeforeSigned),
	fixationEndBeforeStart: t(
		Resources.Portfolio.AddShared.Validations.fixationEndBeforeStart,
	),
});

export const getBusinessLoansFormSchema = (t: TranslateFunction) =>
	z
		.object({
			...getBusinessLoansBaseShape(t),
			change: getBusinessLoansChangeShape(t),
		})
		.superRefine((data, ctx) =>
			refineDateOrder(getDateOrderMessages(t), ["change", "fixationEndDate"])(
				{
					signedAt: data.signedAt,
					startAt: data.startAt,
					endAt: data.endAt,
					fixationEndDate: data.change.fixationEndDate,
				},
				ctx,
			),
		)
		.superRefine(refineContractRequiredMinimum);

export type BusinessLoansFormInput = z.input<
	ReturnType<typeof getBusinessLoansFormSchema>
>;
export type BusinessLoansFormOutput = z.output<
	ReturnType<typeof getBusinessLoansFormSchema>
>;
